import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["processingStart", "startTime"];
import CartographerReporter, { THIRTY_SECONDS } from './CartographerReporter';
import Raven from 'raven-js';
import { onVisibilityHidden } from '../visibility';
import { resetFirstInputPolyfill, firstInputPolyfill } from '../BFCacheRestore';
import performanceNow from '../../vendor/performanceNow';
import PerformanceEventBus from '../PerformanceEventBus';
import PageStateTracker from '../PageStateTracker';
const onBFCacheRestore = callback => {
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      callback(event);
    }
  }, true);
};
const GENERIC_TAGS = new Set(['div', 'span', 'section', 'main', 'header', 'footer', 'nav', 'article', 'aside', 'p', 'li', 'ul', 'ol']);
const MAX_CLASSES = 3;
const MAX_ANCESTOR_DEPTH = 5;
const MAX_ARIA_LABEL_LENGTH = 40;
function getAttr(el, attr) {
  return el.getAttribute(attr) || '';
}
function buildSelfSelector(el) {
  var _el$tagName, _el$tagName$toLowerCa;
  const tag = (_el$tagName = el.tagName) === null || _el$tagName === void 0 || (_el$tagName$toLowerCa = _el$tagName.toLowerCase) === null || _el$tagName$toLowerCa === void 0 ? void 0 : _el$tagName$toLowerCa.call(_el$tagName);
  if (!tag) {
    return '';
  }
  let selector = tag;
  if (el.id) {
    selector += `#${el.id}`;
  }
  const classNames = getAttr(el, 'class');
  if (classNames.trim()) {
    const classes = classNames.trim().split(/\s+/);
    selector += `.${classes.slice(0, MAX_CLASSES).join('.')}`;
    if (classes.length > MAX_CLASSES) {
      selector += '...';
    }
  }
  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    selector += `[data-test-id="${testId}"]`;
  }
  const role = getAttr(el, 'role');
  if (role && role !== 'presentation' && role !== 'none') {
    selector += `[role="${role}"]`;
  }
  const ariaLabel = getAttr(el, 'aria-label');
  if (ariaLabel) {
    const label = ariaLabel.length > MAX_ARIA_LABEL_LENGTH ? `${ariaLabel.slice(0, MAX_ARIA_LABEL_LENGTH)}...` : ariaLabel;
    selector += `[aria-label="${label}"]`;
  }
  if (['input', 'textarea', 'select'].includes(tag)) {
    const name = getAttr(el, 'name');
    if (name) {
      selector += `[name="${name}"]`;
    }
  }
  if (tag === 'img') {
    const alt = getAttr(el, 'alt');
    if (alt) {
      const truncated = alt.length > MAX_ARIA_LABEL_LENGTH ? `${alt.slice(0, MAX_ARIA_LABEL_LENGTH)}...` : alt;
      selector += `[alt="${truncated}"]`;
    }
  }
  return selector;
}
function isDescriptive(selector, tag) {
  if (!selector) {
    return false;
  }
  return selector !== tag || !GENERIC_TAGS.has(tag);
}
function buildAncestorPrefix(el) {
  var _el$tagName2, _el$tagName2$toLowerC;
  const tag = (_el$tagName2 = el.tagName) === null || _el$tagName2 === void 0 || (_el$tagName2$toLowerC = _el$tagName2.toLowerCase) === null || _el$tagName2$toLowerC === void 0 ? void 0 : _el$tagName2$toLowerC.call(_el$tagName2);
  if (!tag) {
    return '';
  }
  let prefix = tag;
  if (el.id) {
    prefix += `#${el.id}`;
  }
  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    prefix += `[data-test-id="${testId}"]`;
  }
  const idMarker = getAttr(el, 'data-id-marker');
  if (idMarker) {
    prefix += `[data-id-marker="${idMarker}"]`;
  }
  if (!testId && !idMarker) {
    const classNames = getAttr(el, 'class');
    if (classNames.trim()) {
      prefix += `.${classNames.trim().split(/\s+/)[0]}`;
    }
  }
  return prefix;
}
function getElementSelector(node) {
  if (!node || !('tagName' in node)) {
    return '';
  }
  const el = node;
  const selfSelector = buildSelfSelector(el);
  let ancestorPart = '';
  let current = el.parentElement;
  let depth = 0;
  while (current && depth < MAX_ANCESTOR_DEPTH) {
    var _current$tagName$toLo, _current$tagName, _current$tagName$toLo2, _current$tagName2;
    depth++;
    const ancestorPrefix = buildAncestorPrefix(current);
    const ancestorTag = (_current$tagName$toLo = (_current$tagName = current.tagName) === null || _current$tagName === void 0 || (_current$tagName$toLo2 = (_current$tagName2 = _current$tagName).toLowerCase) === null || _current$tagName$toLo2 === void 0 ? void 0 : _current$tagName$toLo2.call(_current$tagName2)) !== null && _current$tagName$toLo !== void 0 ? _current$tagName$toLo : '';
    if (isDescriptive(ancestorPrefix, ancestorTag)) {
      const separator = depth === 1 ? ' > ' : ' .. ';
      ancestorPart = ancestorPrefix + separator;
      break;
    }
    current = current.parentElement;
  }
  return ancestorPart + selfSelector;
}
export default class VitalsReporter extends CartographerReporter {
  /**
   * Get the singleton instance of VitalsReporter.
   * Uses window-scoped storage so the instance is shared across all JS bundles
   * on the same page, preventing duplicate LCP reports.
   */
  static getInstance(options) {
    const w = window;
    if (!w[VitalsReporter.WINDOW_KEY]) {
      w[VitalsReporter.WINDOW_KEY] = new VitalsReporter(options);
    }
    return w[VitalsReporter.WINDOW_KEY];
  }
  static resetInstance() {
    window[VitalsReporter.WINDOW_KEY] = undefined;
  }
  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();
    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline
    };
  }
  constructor(options) {
    super(options);
    this.resolved = {};
    this.stopped = false;
    this.firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
    this.interactionMap = new Map();
    this.latestLCP = null;
    window.addEventListener('visibilitychange', event => {
      this.firstHiddenTime = Math.min(this.firstHiddenTime, event.timeStamp);
    }, {
      once: true
    });
    const debounce = () => {
      clearTimeout(this.flushQueueTimeout);
      this.flushQueueTimeout = setTimeout(() => {
        this.flushPerformanceQueue();
        // clear the map once the performance action buffer is cleared, to avoid memory leak
        this.interactionMap.clear();
      }, THIRTY_SECONDS);
    };
    const processFIDEntry = perfEntry => {
      try {
        // some browsers no longer support FID and send an error instead of a PerformanceEventTiming object
        if (perfEntry && typeof perfEntry === 'object' && 'toJSON' in perfEntry && typeof perfEntry.toJSON === 'function') {
          const _toJSON = perfEntry.toJSON(),
            {
              processingStart: processingStart,
              startTime: startTime
            } = _toJSON,
            perfData = _objectWithoutPropertiesLoose(_toJSON, _excluded);
          // ignore entries if the page was not in the foreground when the first input occured
          if (startTime < this.firstHiddenTime) {
            const inputDelay = processingStart - startTime;
            this.pushPerformanceAction(this.lastRouteInfo, 'first-input', Object.assign({}, perfData, {
              processingStart,
              startTime,
              inputDelay,
              pageState: this.getPageState()
            }));
            debounce();
          }
        }
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    const processUserInteractionEntry = perfEntry => {
      try {
        const {
          interactionId,
          target,
          name
        } = perfEntry;
        if (interactionId > 0) {
          let interaction = this.interactionMap.get(interactionId);
          if (!interaction) {
            interaction = {
              latency: 0,
              entries: [],
              interactionNames: ''
            };
            this.interactionMap.set(interactionId, interaction);
          }
          interaction.entries.push(perfEntry);
          interaction.latency = Math.max(perfEntry.duration, interaction.latency);
          const testId = target && target !== null && target !== void 0 && target.hasAttribute('data-test-id') ? target === null || target === void 0 ? void 0 : target.getAttribute('data-test-id') : '';
          const tagName = getElementSelector(target);
          if (name && name.length > 0) {
            interaction.interactionNames = interaction.interactionNames.concat(interaction.interactionNames.length > 0 ? ' ' : '', name);
          }
          this.evictPerformanceAction(interactionId);
          this.pushPerformanceAction(this.lastRouteInfo, 'event', {
            interactionId,
            interactionNames: interaction.interactionNames,
            latency: interaction.latency,
            elapsedMs: performanceNow(),
            testId,
            tagName,
            pageState: this.getPageState()
          });
          debounce();
        }
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    const entryHandler = entry => {
      processFIDEntry(entry);
    };

    /*
      When the page is restored from the back/forward cache, re-add event listeners for FID
      and manually push FID data to the performance actions queue since the performance observer
      won't be able to catch it.
    */
    onBFCacheRestore(() => {
      resetFirstInputPolyfill();
      firstInputPolyfill(entryHandler);
    });

    // Observe performance entries
    PerformanceEventBus.getInstance().subscribe('long-animation-frame', perfEntry => {
      try {
        // For each script in the long animation frame, emit a longtask event
        // Only log scripts with duration >= 50ms
        perfEntry.scripts.forEach(script => {
          if (script.duration >= 50) {
            const scriptData = script.toJSON();
            this.pushPerformanceAction(this.lastRouteInfo, 'longtask', Object.assign({
              name: script.windowAttribution
            }, scriptData, {
              entryType: 'longtask',
              pageState: this.getPageState()
            }));
          }
        });
        debounce();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
    PerformanceEventBus.getInstance().subscribe('first-input', perfEntry => {
      processFIDEntry(perfEntry);
    });
    PerformanceEventBus.getInstance().subscribe('event', perfEntry => {
      processUserInteractionEntry(perfEntry);
    });
    PerformanceEventBus.getInstance().subscribe('paint', perfEntry => {
      try {
        if (perfEntry.name === 'first-contentful-paint') {
          const paintData = {
            name: perfEntry.name,
            startTime: perfEntry.startTime,
            entryType: perfEntry.entryType,
            elapsedMs: performanceNow(),
            pageState: this.getPageState()
          };
          this.pushPerformanceAction(this.lastRouteInfo, 'paint', paintData);
          debounce();
        }
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
    PerformanceEventBus.getInstance().subscribe('largest-contentful-paint', perfEntry => {
      try {
        const element = perfEntry.element;
        const {
          entryType,
          size,
          startTime,
          renderTime,
          loadTime,
          id,
          url
        } = perfEntry.toJSON();
        const paintData = {
          entryType,
          size,
          startTime,
          renderTime,
          element: getElementSelector(element !== null && element !== void 0 ? element : null),
          loadTime,
          elapsedMs: performanceNow(),
          id,
          url,
          pageState: this.getPageState()
        };
        // Store the latest LCP instead of pushing immediately
        // It will be pushed when the page becomes hidden or user interacts
        this.latestLCP = {
          route: this.lastRouteInfo,
          data: paintData
        };
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });

    // Handle first user interaction to finalize LCP
    const finalizeOnInteraction = () => {
      try {
        if (this.latestLCP) {
          this.pushPerformanceAction(this.latestLCP.route, 'largest-contentful-paint', this.latestLCP.data);
          this.latestLCP = null;
          debounce();
        }
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };
    PerformanceEventBus.getInstance().subscribe('layout-shift', perfEntry => {
      try {
        var _perfEntry$sources;
        // Filter out user-initiated shifts and insignificant shifts
        if (perfEntry.hadRecentInput || perfEntry.value < 0.001) {
          return;
        }
        const {
          entryType,
          value,
          startTime,
          hadRecentInput,
          lastInputTime
        } = perfEntry.toJSON();
        const sources = ((_perfEntry$sources = perfEntry.sources) !== null && _perfEntry$sources !== void 0 ? _perfEntry$sources : []).map(source => ({
          element: getElementSelector(source.node),
          previousRect: source.previousRect,
          currentRect: source.currentRect
        }));
        const layoutShiftData = {
          entryType,
          value,
          startTime,
          sources,
          hadRecentInput,
          lastInputTime,
          elapsedMs: performanceNow(),
          pageState: this.getPageState()
        };
        this.pushPerformanceAction(this.lastRouteInfo, 'layout-shift', layoutShiftData);
        debounce();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
    // Store cleanup functions for interaction listeners
    const interactionCleanupFunctions = [];

    // Listen for first interaction events that finalize LCP
    const eventTypes = ['keydown', 'mousedown', 'pointerdown', 'touchstart'];
    eventTypes.forEach(eventType => {
      const handler = () => {
        finalizeOnInteraction();
        // Clean up remaining listeners after first interaction
        interactionCleanupFunctions.forEach(cleanup => cleanup());
      };
      window.addEventListener(eventType, handler, {
        capture: true,
        once: true
      });
      interactionCleanupFunctions.push(() => window.removeEventListener(eventType, handler, {
        capture: true
      }));
    });

    // Handle visibility change to flush remaining entries
    onVisibilityHidden(() => {
      try {
        // Push the final LCP before flushing
        if (this.latestLCP) {
          this.pushPerformanceAction(this.latestLCP.route, 'largest-contentful-paint', this.latestLCP.data);
          this.latestLCP = null;
        }
        // Clean up interaction listeners since LCP is now finalized
        interactionCleanupFunctions.forEach(cleanup => cleanup());
        // Flush any remaining performance actions
        this.flushPerformanceQueue();
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }
  destroy() {
    // VitalsReporter is a singleton shared across all RhumbProvider instances;
    // its listeners must persist for the page lifetime.
  }
  evictPerformanceAction(interactionId) {
    this.performanceActions = this.performanceActions.filter(action => action.data.interactionId !== interactionId);
  }
  __setFirstHiddenTime(time) {
    this.firstHiddenTime = time;
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || this.resolved[action.payload.entry.id] || this.stopped) {
      return;
    }
    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED':
        {
          const {
            routeSpec: {
              route
            }
          } = action.payload;
          this.lastRouteInfo = {
            route
          };
          break;
        }
      case 'ROUTE_FAILED':
        {
          const {
            entry: {
              pathname
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route
          };
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
        {
          const {
            entry: {
              pathname
            },
            extra: {
              scenario
            },
            routeSpec
          } = action.payload;
          const {
            route
          } = routeSpec;
          this.lastRouteInfo = {
            pathname,
            route,
            scenario
          };
          break;
        }
      default:
    }
    switch (action.type) {
      case 'ROUTE_UNEXPECTED':
        {
          this.stopped = true;
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED':
        {
          this.resolved[action.payload.entry.id] = true;
          break;
        }
      default:
    }
  }
}
VitalsReporter.WINDOW_KEY = '__rhumb_vitals_reporter__';